import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ClosetDoorsCustomService } from 'src/app/services/configurators-2d/applications/closet-doors-custom.service';
import { CurrentService } from 'src/app/services/current.service';
import { IPreview } from '../../../configurator/previews/i-preview';
import { Configuration } from '../../../../../models/configurators-2d/applications/closet-doors-custom/configuration/configuration';
import { PreviewItem } from '../../../configurator/previews/preview-item';
import { GrainDirectionEnum } from '../../../../../enum/closet-doors-custom/grain-direction-enum';

@Component({
  selector: 'ng-closet-doors-custom-preview',
  templateUrl: './preview.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ClosetDoorsCustomPreviewComponent
  extends PreviewItem<Configuration, ClosetDoorsCustomService>
  implements OnInit, IPreview
{
  finishesStep: number = 1;

  @Input() step: number;

  @Output() onDoorSelect: EventEmitter<{ selectedDoor: number; selectedSection: number }> = new EventEmitter<{
    selectedDoor: number;
    selectedSection: number;
  }>();

  /** Objet preview pour l'affichage */
  preview: any[];
  /** Objet profils, les tailles sont exprimées en millimètres */
  profiles: any[];
  /** Objet contenant les décors à afficher sur les portes */
  decorations: any[];

  /** Porte sélectionnée */
  selectedDoor: number;
  /** Zone de la porte sélectionée */
  selectedSection: number;
  /** URL des images */
  baseImgURL: string;

  isPreviewLoading: boolean;

  doorStructurePreviewItems: any[];

  doorHeight: number;

  previewRatio: number = 0;

  handle = {
    path: '/assets/img/closet-doors/Visuels/poignee.png',
    height: 90,
    width: 20,
    left: 20,
  };

  constructor(
    public current: CurrentService,
    public closetDoorCustomService: ClosetDoorsCustomService
  ) {
    super(closetDoorCustomService);
    this.baseImgURL = this.service.apiUrl;
  }

  ngOnInit(): void {
    if (this.step === 4) {
      this.selectDecoration(1, 1);
    }
  }

  /**
   * Mise à jour de la preview
   */
  async update(): Promise<void> {
    let preview = this.computePreview();
    this.preview = preview;

    setTimeout(() => {
      this.preview = preview;
    }, 10);
  }

  private computePreview(): any[] {
    let preview = [];
    this.doorHeight = 0;
    this.previewRatio = 0;

    if (this.configuration.configurationDoorStructures && this.doorStructurePreviewItems) {
      for (let i = 0; i < this.configuration.nbDoor; i++) {
        preview.push({
          position: i + 1,
          product: {
            type: 'door',
            isOffset: i === 0 ? false : true, // calcul temporaire
            offsetValue: 5,
            orderFromFront: this.calculateOrderFromFront(i, this.configuration.nbDoor), // calcul temporaire
            items: this.doorStructurePreviewItems.find(
              item => item.doorStructureId === this.configuration.configurationDoorStructures[i].doorStructureId
            )?.items,
          },
        });
      }
    }

    // Calcul de la hauteur d'un vantail
    if (preview.length > 0) {
      let sectionTops: number[] = [];
      const door = preview[0];
      if (door?.product?.items) {
        for (const item of door.product.items) {
          // profilé haut ou rail bas
          if (item.product.type.includes('profile') || item.product.type.includes('wheel-bar')) {
            this.doorHeight += this.calculateElementMmToPx(item.product.height);
          }
          // corps du vantail
          else if (item.product.type.includes('body')) {
            for (const bodyItem of item.product.items) {
              if (bodyItem.product.type.includes('decor')) {
                for (const decorItem of bodyItem.product.items) {
                  // traverse, section ou profilé bas
                  if (
                    (decorItem.product.type.includes('crosstie-horizontal') && decorItem.product.inDoorHeight) ||
                    decorItem.product.type.includes('bottom-profile')
                  ) {
                    this.doorHeight += this.calculateElementMmToPx(decorItem.product.height);
                  } else if (decorItem.product.type.includes('door-section')) {
                    // ne pas compter les sections des portes avec traverses verticales
                    if (!sectionTops.some(t => t === decorItem.product.top)) {
                      sectionTops.push(decorItem.product.top);
                      this.doorHeight += this.calculateElementMmToPx(decorItem.product.height);
                    }
                  }
                }
              }
            }
          }
        }
      }

      if (this.doorHeight === 0) {
        // provoque un événement pour recalculer la hauteur du vantail
        setTimeout(() => this.computePreview(), 10);
      }
    }

    return preview;
  }

  calculateOrderFromFront(i: number, nbDoors: number): number {
    if (this.configuration.percentageOpening) {
      if (this.configuration.percentageOpening === 66) {
        return i + 1;
      }

      if (nbDoors === 4) {
        return i == 1 || i == 2 ? 1 : 2;
      }

      return i % 2 === 0 ? 1 : 2;
    } else {
      return 1;
    }
  }

  hasHandle(): boolean {
    if (this.configuration.openingSystem && this.configuration.openingSystem.type > 0) {
      return true;
    } else {
      return false;
    }
  }

  getOpeningSystemStr(): string {
    if (this.configuration.openingSystem.type === 0) {
      return 'coulissant';
    } else if (this.configuration.openingSystem.type === 1) {
      return 'pivotant';
    } else if (this.configuration.openingSystem.type === 2) {
      return 'pliant';
    }
  }

  handleSide(doorIndex?: number): string {
    let res = '';

    if (this.configuration.openingSystemSide) {
      res += this.getOpeningSystemStr();

      let name = this.configuration.openingSystemSide.name.replace('Tirage ', '');

      /* centre, gauche, droit, centre droit, centre gauche */
      switch (doorIndex) {
        case 1:
          res += name.startsWith('centre') || name.startsWith('gauche') ? '-right' : '-left';
          break;
        case 2:
          res += name == 'centre' || name == 'droit' || name == 'centre droit' ? '-left' : '-right';
          break;
        case 3:
          res += '-left';
          break;
      }
    }

    return res;
  }

  /**
   * Fonction de sélection d'une zone de la porte
   * @param doorId position de la porte sélectionnée
   * @param sectionId id de la zone à sélectionner
   */
  selectDecoration(doorId: number, sectionId: number): void {
    if (this.step === 4 && (this.finishesStep === 1 || this.finishesStep === 2)) {
      if (doorId === this.selectedDoor && sectionId === this.selectedSection) {
        this.selectedDoor = undefined;
        this.selectedSection = undefined;
      } else {
        this.selectedDoor = doorId;
        this.selectedSection = sectionId;
      }

      this.onDoorSelect.emit({ selectedDoor: this.selectedDoor, selectedSection: this.selectedSection });
    }
  }

  /**
   * Fonction de changement de la classe css si la section est active ou non.
   * @param area
   * @param position
   * @returns
   */
  isSectionSelected(area: any, position: number): boolean {
    let isSelected: boolean;
    if (this.configuration.doorRange.hasSameDoor) {
      if (this.configuration.doorRange.sameDoorFrameIds.length > 0)
        if (this.configuration.doorRange.sameDoorFrameIds.includes(this.configuration.frame?.id))
          isSelected = this.selectedSection === area;
        else isSelected = this.selectedSection === area && this.selectedDoor === position;
      else isSelected = this.selectedSection === area;
    } else {
      isSelected = this.selectedSection === area && this.selectedDoor === position;
    }
    return isSelected;
  }

  getProfilesImages(): void {
    this.isPreviewLoading = true;
    this.preview = null;

    this.service.getPreview().then(async res => {
      this.doorStructurePreviewItems = res;
      this.isPreviewLoading = false;
      await this.update();
    });
  }

  /**
   * Fonction de calcul des pixels pour recouvrement
   * @param id id du vantail
   * @returns pixels de recouvrement du vantail
   */
  calculateOffset(percentOffset: number): number {
    return this.calculateDoorWidth() * (percentOffset / 100);
  }

  /**
   * Calcul de la largeur d'une porte en fonction de la taille saisie dans la configuration
   * @returns largeur en pixels d'un vantail
   */
  calculateDoorWidth(): number {
    this.computeRatio();
    if (this.previewRatio) {
      const doorWidth = this.configuration.clientWidth / this.configuration.nbDoor;
      return doorWidth * this.previewRatio;
    }
    return 0;
  }

  /**
   * Calcul de la taille d'un élément en pixels
   * @param size taille de l'élément en millimètres
   * @returns taille de l'élément en pixels
   */
  calculateElementMmToPx(size: number): number {
    this.computeRatio();

    if (this.previewRatio && size > 0) {
      let result = size * this.previewRatio;
      return result >= 1 ? result : 1;
    }

    return 0;
  }

  private computeRatio() {
    if (!this.previewRatio) {
      const previewContainer = document.getElementById('previewContainer');
      if (previewContainer) {
        // 90% du container (la taille du layer)
        const clientWidth = previewContainer.clientWidth * 0.9;
        const clientHeight = previewContainer.clientHeight * 0.9;

        let convertRatio: number;

        // configuration paysage
        if (this.configuration.clientWidth > this.configuration.clientHeight) {
          convertRatio = clientWidth / this.configuration.clientWidth;
        }
        // configuration carré
        else if (this.configuration.clientWidth === this.configuration.clientHeight) {
          // preview paysage
          if (clientWidth > clientHeight) {
            convertRatio = clientHeight / this.configuration.clientHeight;
          }
          // preview portrait ou carré
          else {
            convertRatio = clientWidth / this.configuration.clientWidth;
          }
        }
        // configuration portrait
        else {
          convertRatio = clientHeight / this.configuration.clientHeight;
        }

        this.previewRatio = convertRatio;
      }
    }
  }

  /** GESTION DES URLS */
  getBackgroundImage(): string {
    let path: string = undefined;

    if (this.configuration?.doorRange != null && this.step === 3) {
      path = this.configuration?.doorRange?.icon?.path;
    } else {
      path = this.configuration?.location?.picture?.path;
    }

    return path ? 'url(' + this.baseImgURL + path + ')' : '';
  }

  /**
   * Fonction de récupération du décor de l'image
   * @param decoration
   * @param doorId
   * @returns url de l'image
   */
  getDecorImg(decoration: any, doorId: number): string {
    if (this.configuration.configurationDoorStructures) {
      let section = this.configuration.configurationDoorStructures[doorId - 1]?.configurationDoorSections.find(
        s => s.doorSection.index === decoration.area
      );

      if (section == null) {
        section = this.configuration.configurationDoorStructures[doorId - 1]?.configurationDoorSections.find(
          s => s.doorSection.index === decoration.area
        );
      }

      if (section) {
        let path = section.colorComputedPath ?? section.color.picture.path;
        path = path ? this.baseImgURL + path : '';

        // c'est un bois avec un sens de veinage horizontal
        if (this.isHorizontalGrainDirection(decoration.area, doorId)) {
          path = section.color?.rotatePicture?.path;
        }

        // if(decoration.isMirror || decoration.isGlass){
        //     applyBgTransparency(decoration.isGlass,
        //                         decoration.isMirror,
        //                         decoration.opacity,
        //                         path,
        //                         decoration.width,
        //                         decoration.height,
        //                         decoration.top,
        //                         decoration.left,
        //                         decoration.position,
        //                         this.configuration.clientWidth / this.configuration.nbDoor,
        //                         this.configuration.clientWidth, this.configuration.clientHeight).then(res => {
        //         return res;
        //     })
        // } else {
        //     return path ? this.baseImgURL + path : '';
        // }

        return path;
      }
    }
  }

  /**
   * Permet de savoir dans quel sens afficher le décor
   * @param sectionId
   * @param doorId
   * @private
   */
  private isHorizontalGrainDirection(sectionId: any, doorId: number): boolean {
    if (this.configuration.configurationDoorStructures) {
      let configurationDoorStructure = this.configuration.configurationDoorStructures.find(
        cds => cds.displayOrder === doorId
      );
      let doorStructure = configurationDoorStructure.doorStructure;
      let color = configurationDoorStructure.configurationDoorSections.find(
        cds => cds.doorSection.index === sectionId
      ).color;

      if (
        (doorStructure.defaultGrainDirection != null ||
          this.configuration.doorRange.defaultMixageGrainDirection != null) &&
        color.isWood &&
        color.needRotationIfRatioBelow1 != true
      ) {
        // si c'est un mixage de portes différentes
        let isMixage = !this.configuration.configurationDoorStructures
          .map(cds => cds.doorStructure.id)
          .every(id => id === doorStructure.id);

        if (isMixage && this.configuration.doorRange.defaultMixageGrainDirection === GrainDirectionEnum.horizontal) {
          return true;
        } else if (!isMixage && doorStructure.defaultGrainDirection === GrainDirectionEnum.horizontal) {
          return true;
        }
      }
    }

    return false;
  }
}
